import React, { FC, ChangeEvent, useState, useEffect, useRef } from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';

import { AutocompleteDropdown, CustomModal, RhfTextField } from '@components';
import { OptionsLOVType } from '@components/auto-complete/types';
import { SHORT_FIELD_MAX_LENGTH, API_RESPONSE_STATUS } from '@constants/common';
import { getNumbersOnly } from '@utils/common';
import { translate } from '@utils/locale';
import { useLazyGetStateQuery, useLazyGetCityQuery } from '@utils/api';
import { ToastTypes } from '@type/toast';
import displayToast from '@components/toast/customToast';
import FormDualButton from '@components/form-dual-button/FormDualButton';

import { SectionMapper } from '../constants';
import {
  useCreateCompanyAddressMutation,
  useUpdateCompanyAddressMutation
} from '../api';
import { Country, TimeZone } from '@type/util';

import { Company } from '../types';
import { showGenericErrorToast } from '@utils/error';

const AddressSection: FC<{
  editMode?: SectionMapper;
  updateEditMode?: (section?: SectionMapper) => void;
  disableEdit?: boolean;
  companyId: string;
  countriesList: Country[] | undefined;
  timeZoneList: TimeZone[] | undefined;
  companyDetails: Company;
  getResetAddressSection: (formValues: FieldValues) => {
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    state: string | null;
    country: string;
    pin: string | null;
    timezone: string | null;
  };
  getResetFunction: (
    editMode?: SectionMapper
  ) => (formValues: FieldValues) => {};
}> = ({
  editMode,
  updateEditMode,
  disableEdit,
  companyDetails,
  companyId,
  countriesList,
  getResetAddressSection,
  getResetFunction,
  timeZoneList
}) => {
  const [isUpdateFlow, setIsUpdateFlow] = useState(false);

  const {
    control,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
    trigger,
    reset
  } = useFormContext();

  const addressLine1Ref = useRef<HTMLInputElement | null>(null);

  const selectedCountry = useWatch({
    name: 'country'
  });
  const selectedState = useWatch({
    name: 'state'
  });
  const selectedCity = useWatch({
    name: 'city'
  });
  const selectedTimezone = useWatch({
    name: 'timezone'
  });

  const [createCompanyAddress, { isLoading: isCreateLoading }] =
    useCreateCompanyAddressMutation();

  const [updateCompanyAddress, { isLoading: isUpdateLoading }] =
    useUpdateCompanyAddressMutation();

  const [getState, { data: stateList }] = useLazyGetStateQuery();

  const [getCity, { data: cityList }] = useLazyGetCityQuery();

  const [isConfirmModalOpen, setConfirmModalOpen] = useState<
    'discard' | 'save' | undefined
  >();

  const [isCancelClicked, setIsCancelClicked] = useState<boolean>(true);

  const isEditMode = editMode === SectionMapper.address;

  const countryValues =
    countriesList?.map(val => ({ id: val.name, name: val.name })) ?? [];
  const stateValues =
    stateList?.map(val => ({ id: val.name, name: val.name })) ?? [];
  const cityValues =
    cityList?.map(val => ({ id: val.name, name: val.name })) ?? [];
  const timeZoneValues =
    timeZoneList?.map(val => ({ id: val.name, name: val.name })) ?? [];

  const onCountrySelect = (selectedItem: OptionsLOVType) => {
    if (selectedItem?.name)
      getState({ searchTerm: '', country: selectedItem.name });
    setValue('state', '');
    setValue('city', '');
    clearErrors('country');
  };

  const onStateSelect = (selectedItem: OptionsLOVType) => {
    if (selectedItem?.name) {
      getCity({
        searchTerm: '',
        country: getValues('country'),
        state: selectedItem.name
      });
    }
  };

  const onAddressLine1TextFieldClick = () => clearErrors('addressLine1');

  const onAddressLine2TextFieldClick = () => clearErrors('addressLine2');

  const onPinChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    clearErrors('pin');
    setValue('pin', getNumbersOnly(event.target.value));
  };

  const onEditSaveButtonClick = async () => {
    if (!editMode) {
      updateEditMode?.(SectionMapper.address);
      return;
    }

    if (editMode !== SectionMapper.address) {
      setConfirmModalOpen('discard');
      return;
    }
    const isFormValid = await trigger([
      'addressLine1',
      'addressLine2',
      'city',
      'state',
      'country',
      'pin',
      'timezone'
    ]);
    if (isFormValid) {
      setConfirmModalOpen('save');
    }
  };

  const onUpdateDetails = async () => {
    const values = getValues();
    const payload = {
      company_id: companyId,
      payload: {
        address_line_1: values.addressLine1,
        address_line_2: values.addressLine2,
        city: values.city,
        state: values.state,
        country: values.country,
        pin: values.pin ? Number(values.pin) : undefined,
        time_zone: values.timezone || null
      }
    };
    if (!isUpdateFlow) {
      try {
        const response = await createCompanyAddress(payload).unwrap();
        if (response && response.status === API_RESPONSE_STATUS.OK) {
          displayToast(ToastTypes.SUCCESS, 'Address Updated Successfully');
          updateEditMode?.();
          setConfirmModalOpen(undefined);
          setIsUpdateFlow(true);
        } else {
          displayToast(
            ToastTypes.ERROR,
            response.error?.details?.[0] ?? 'Failed to Update Address'
          );
        }
      } catch (err) {
        showGenericErrorToast(err);
      }
      return;
    }
    try {
      const response = await updateCompanyAddress(payload).unwrap();
      if (response && response.status === API_RESPONSE_STATUS.OK) {
        displayToast(ToastTypes.SUCCESS, 'Address Updated Successfully');
        updateEditMode?.();
        setConfirmModalOpen(undefined);
      } else {
        displayToast(
          ToastTypes.ERROR,
          response.error?.details?.[0] ?? 'Failed to Update Address'
        );
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
  };

  const onDiscardDetails = () => {
    const values = getValues();
    reset(getResetFunction(editMode)(values));
    isCancelClicked
      ? updateEditMode?.()
      : updateEditMode?.(SectionMapper.address);
    setConfirmModalOpen(undefined);
  };

  const onClickCancelButton = () => {
    setIsCancelClicked(true);
    setConfirmModalOpen('discard');
  };

  useEffect(() => {
    addressLine1Ref.current?.focus();
  }, [isEditMode]);

  useEffect(() => {
    reset(getResetAddressSection(getValues()));
  }, [companyDetails]);

  useEffect(() => {
    if (companyDetails?.address?.id) {
      setIsUpdateFlow(true);
    }
    if (companyDetails?.address?.country) {
      getState({
        searchTerm: '',
        country: companyDetails?.address?.country
      });
      if (companyDetails?.address?.state)
        getCity({
          searchTerm: '',
          country: companyDetails?.address?.country,
          state: companyDetails?.address?.state
        });
    }
  }, [companyDetails]);

  return (
    <>
      <div className="flex flex-row justify-between items-center">
        <div className="text-xl font-semibold">
          {translate('company.addressSection.title')}
        </div>
        <FormDualButton
          enableEdit={!disableEdit}
          showCancelOrSaveButton={isEditMode}
          onClickCancelButton={onClickCancelButton}
          onClickEditButton={onEditSaveButtonClick}
        />
      </div>
      <div className="w-full lg:w-[70%]">
        <div className="flex flex-row mr-6">
          <RhfTextField
            name="addressLine1"
            label={translate('company.addressSection.addressLine1')}
            placeholder={translate('company.placeholders.registeredAddress')}
            control={control}
            errors={errors}
            wrapperClass="pt-4 w-full"
            onClickTextField={onAddressLine1TextFieldClick}
            maxLength={SHORT_FIELD_MAX_LENGTH}
            isDisabled={!isEditMode}
            description={translate('company.description.addressLine1')}
            multiline
            rows={3}
            showCharacterLimit
            fieldRef={addressLine1Ref}
          />
        </div>
        <div className="flex flex-row mr-6">
          <RhfTextField
            name="addressLine2"
            label={translate('company.addressSection.addressLine2')}
            placeholder={translate('company.placeholders.registeredAddress')}
            control={control}
            errors={errors}
            wrapperClass="pt-6 w-full"
            onClickTextField={onAddressLine2TextFieldClick}
            maxLength={SHORT_FIELD_MAX_LENGTH}
            isDisabled={!isEditMode}
            multiline
            rows={3}
            showCharacterLimit
          />
        </div>
        <div className="flex flex-row mr-6">
          <AutocompleteDropdown
            control={control}
            name="country"
            options={countryValues}
            errors={errors}
            selectedItem={countryValues.find(
              value => value.id === selectedCountry
            )}
            placeholder={translate('company.addressSection.country')}
            wrapperClass="pt-6 mr-6 w-full"
            isDisabled={!isEditMode}
            onChangeHandler={onCountrySelect}
          />
          <AutocompleteDropdown
            control={control}
            name="state"
            options={stateValues}
            errors={errors}
            selectedItem={stateValues.find(value => value.id === selectedState)}
            placeholder={translate('company.addressSection.state')}
            wrapperClass="pt-6 mr-6 w-full"
            isDisabled={!isEditMode || !selectedCountry}
            onChangeHandler={onStateSelect}
          />
          <AutocompleteDropdown
            control={control}
            name="city"
            options={cityValues}
            errors={errors}
            selectedItem={cityValues.find(value => value.id === selectedCity)}
            placeholder={translate('company.addressSection.city')}
            wrapperClass="pt-6 w-full"
            isDisabled={!isEditMode || !selectedState}
          />
        </div>
        <div className="flex flex-row mr-6 w-[66.66%]">
          <RhfTextField
            name="pin"
            label={translate('company.addressSection.pin')}
            placeholder={translate('company.placeholders.pinCode')}
            control={control}
            errors={errors}
            wrapperClass="pt-6 mr-6 w-full"
            onChange={onPinChange}
            maxLength={10}
            isDisabled={!isEditMode}
            showCharacterLimit
          />
          <AutocompleteDropdown
            control={control}
            name="timezone"
            options={timeZoneValues}
            errors={errors}
            selectedItem={timeZoneValues.find(
              value => value.id === selectedTimezone
            )}
            placeholder={translate('company.addressSection.timezone')}
            wrapperClass="pt-6 mr-6 w-full"
            description={translate('company.description.timezone')}
            isDisabled={!isEditMode}
          />
        </div>
      </div>
      {isConfirmModalOpen && (
        <CustomModal
          isModalOpen={!!isConfirmModalOpen}
          header={
            isConfirmModalOpen === 'discard'
              ? translate('modals.discardPopupTitle')
              : translate('modals.saveInfoTitle')
          }
          primaryButtonLabel={
            isConfirmModalOpen === 'discard'
              ? translate('modals.confirm')
              : translate('update')
          }
          secondaryButtonVariant="text"
          secondaryButtonLabel={translate('modals.cancelButtonTitle')}
          onChangePrimaryButton={
            isConfirmModalOpen === 'discard'
              ? onDiscardDetails
              : onUpdateDetails
          }
          onChangeSecondaryButton={() => setConfirmModalOpen(undefined)}
          isPrimaryButtonLoading={isCreateLoading || isUpdateLoading}
        />
      )}
    </>
  );
};

export default AddressSection;
